/**
 * MemberInviteRoute — /bli-med/:token.
 *
 * Et nytt medlem åpner invitasjonslenken på telefonen og velger passordet
 * sitt (#141). Brukernavnet er satt av admin og vises fra forhåndsvisningen.
 *
 *   GET  /api/members/invite/:token         → MemberInvitePreview
 *   POST /api/members/invite/:token/accept  → MemberInviteAccept
 *
 * En invitasjon setter bare det medlemmet mangler (#140): har medlemmet
 * allerede eget passord (`hasPassword`) eller PIN (`hasPin`), svarer serveren
 * `already_enrolled` for det feltet. Derfor:
 *   - mangler passord → passord kreves, PIN tilbys hvis den mangler
 *   - har passord, mangler PIN → bare PIN (de fleste barn har PIN fra før,
 *     så det vanlige er motsatt: bare passord)
 *   - har begge → «du er allerede klar» + lenke til innlogging
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { MemberInvitePreview } from '@stell/shared';
import { Button } from '../../components/primitives';
import { AuthApi, asApiError, splitAuthError } from '../auth/authApi';
import type { ApiError } from '../../lib/api';
import {
  EMPTY_NEW_PASSWORD,
  EMPTY_PIN_PAIR,
  HiddenUsername,
  NewPasswordFields,
  OptionalPinKeypads,
  isNewPasswordValid,
  isPinPairValid,
  type NewPasswordValue,
  type PinPairValue,
} from '../auth/AuthFields';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'invalid' }
  | { kind: 'ready'; preview: MemberInvitePreview };

export function MemberInviteRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token = '' } = useParams();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [password, setPassword] = useState<NewPasswordValue>(EMPTY_NEW_PASSWORD);
  // null = ingen PIN. Når bare PIN mangler, vises tastaturene med en gang.
  const [pin, setPin] = useState<PinPairValue | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ kind: 'invalid' });
      return;
    }
    let cancelled = false;
    AuthApi.invitePreview(token).then(
      (preview) => {
        if (cancelled) return;
        setState({ kind: 'ready', preview });
        if (preview.hasPassword && !preview.hasPin) setPin(EMPTY_PIN_PAIR);
      },
      () => {
        if (!cancelled) setState({ kind: 'invalid' });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [token]);

  const preview = state.kind === 'ready' ? state.preview : null;
  // FF-11 (#179): innloggingen fyller inn familiekoden fra lenken (FF-09).
  const loginPath = preview?.familyCode ? `/login?familie=${encodeURIComponent(preview.familyCode)}` : '/login';
  const needsPassword = preview !== null && !preview.hasPassword;
  const canSubmit =
    !saving &&
    preview !== null &&
    (needsPassword ? isNewPasswordValid(password) : pin !== null) &&
    isPinPairValid(pin);

  async function submit(): Promise<void> {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      await AuthApi.inviteAccept(token, {
        ...(needsPassword
          ? { password: password.password, passwordConfirm: password.confirm }
          : {}),
        ...(pin !== null ? { pin: pin.pin, pinConfirm: pin.pinConfirm } : {}),
      });
      setPassword(EMPTY_NEW_PASSWORD);
      navigate(loginPath, { replace: true });
    } catch (err) {
      setError(asApiError(err));
    } finally {
      setSaving(false);
    }
  }

  if (state.kind === 'loading') {
    return (
      <main className="p-8" data-testid="member-invite-loading">
        <p className="text-ink-2">{t('onboarding.invite.loading')}</p>
      </main>
    );
  }

  if (state.kind === 'invalid' || preview === null) {
    return (
      <main className="p-8 space-y-3" data-testid="member-invite-invalid">
        <h1 className="font-display text-display-s text-ink">{t('onboarding.invite.invalid_title')}</h1>
        <p className="text-sm text-ink-2">{t('onboarding.invite.invalid_body')}</p>
        <Link className="text-primary underline text-sm" to="/login">
          {t('onboarding.invite.back_to_login')}
        </Link>
      </main>
    );
  }

  if (preview.hasPassword && preview.hasPin) {
    return (
      <main className="mx-auto max-w-xl p-8 space-y-3" data-testid="member-invite-done">
        <h1 className="font-display text-display-s text-ink">{t('onboarding.invite.already_title')}</h1>
        <p className="text-sm text-ink-2">
          {t('onboarding.invite.already_body', { name: preview.memberName })}
        </p>
        <Link className="text-primary underline text-sm" to={loginPath}>
          {t('onboarding.invite.back_to_login')}
        </Link>
      </main>
    );
  }

  // Ukjente feil her betyr nesten alltid en brukt eller utløpt invitasjon.
  const errors = splitAuthError(t, error, t('onboarding.invite.invalid_body'));

  return (
    <main className="mx-auto max-w-xl p-6 space-y-5" data-testid="member-invite-route">
      <header className="space-y-2">
        <h1 className="font-display text-display-s text-ink">{t('onboarding.invite.title')}</h1>
        <p className="text-sm text-ink-2">
          {needsPassword
            ? t('onboarding.invite.body', { name: preview.memberName })
            : t('onboarding.invite.pin_only_body', { name: preview.memberName })}
        </p>
        {preview.username ? (
          <p className="text-sm text-ink-2" data-testid="member-invite-username">
            {t('onboarding.invite.username', { username: preview.username })}
          </p>
        ) : null}
        {preview.familyCode ? (
          <p className="text-sm text-ink-2" data-testid="member-invite-family-code">
            {t('onboarding.invite.family_code', { code: preview.familyCode })}
          </p>
        ) : null}
      </header>

      <form
        className="flex flex-col gap-4"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <HiddenUsername value={preview.username} />
        {needsPassword ? (
          <NewPasswordFields
            value={password}
            onChange={setPassword}
            testId="invite-password"
            label={t('auth.password')}
            error={errors.password}
          />
        ) : null}

        {preview.hasPin ? null : (
          <OptionalPinKeypads value={pin} onChange={setPin} testIdPrefix="invite-pin" />
        )}

        {errors.other ? (
          <p className="text-bad text-sm" role="alert" data-testid="member-invite-error">
            {errors.other}
          </p>
        ) : null}

        <div className="flex justify-end">
          <Button type="submit" disabled={!canSubmit} data-testid="member-invite-submit">
            {t('onboarding.invite.submit')}
          </Button>
        </div>
      </form>
    </main>
  );
}
